import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View, Platform } from 'react-native';
import CustomButton from '../common/CustomButton';
import Label, { LabelType } from '../common/Label';
import useTranslation from '../../hooks/translation-hook';
import CustomTextInput, { InputTypeValidation } from '../common/CustomTextInput';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hook';
import { authActions } from '../../models/helpers/platformImportHelper';
import useCustomTheme from '../../hooks/theme-hook';
import { StylesHelper } from '../../models/helpers/stylesHelper';
import Loading from '../common/Loading';
import { getAuth, getMultiFactorResolver, PhoneAuthProvider, RecaptchaVerifier, PhoneMultiFactorGenerator } from 'firebase/auth';

export interface ReauthenticateUserFormProps {
  onReauthenticate: () => void;
}

const ReauthenticateUserForm = (props: ReauthenticateUserFormProps) => {
  const auth = getAuth();
  const t = useTranslation();
  const tLogin = useTranslation({ defaultPath: 'login' });
  const dispatch = useAppDispatch();
  const theme = useCustomTheme();
  const authStore = useAppSelector((selector) => selector.auth);
  const secondInput = useRef(null);
  const popupTitleColor = theme.text.darkTurquoiseTwo?.toString() ?? '#035164';
  const popupBodyColor = theme.text.primary?.toString() ?? '#1a1818';
  const popupButtonColor = theme.background.darkTurquoise?.toString() ?? '#035164';
  const popupTitleStyle = { fontSize: 28, color: popupTitleColor, fontWeight: '600' as const, width: '100%' as const, flexShrink: 0 };
  const popupBodyStyle = {
    marginTop: 12,
    fontSize: 18,
    color: popupBodyColor,
    fontWeight: '400' as const,
    width: '100%' as const,
    flexShrink: 0,
  };
  const popupBodyEmphasisStyle = { ...popupBodyStyle, fontWeight: '600' as const };
  const popupButtonStyle = { marginTop: 24, backgroundColor: popupButtonColor, borderColor: popupButtonColor };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userMessage, setUserMessage] = useState<string | undefined>();
  const [emailLoginValidationError, setEmailLoginValidationError] = useState<string | undefined>(undefined);
  const [passwordLoginValidationError, setPasswordLoginValidationError] = useState<string | undefined>(undefined);
  const [passwordLoginInputValidationError, setPasswordLoginInputValidationError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [phoneVerificationId, setPhoneVerificationId] = useState<string>();
  const [resolver, setResolver] = useState(null);
  const [phoneInfoOptions, setPhoneInfoOptions] = useState<any>({});
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [show2fa, setShow2fa] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeError, setVerificationCodeError] = useState<string | undefined>(undefined);
  const normalizeVerificationCode = (value: string) => value.replace(/\D/g, '').slice(0, 6);

  const getVerificationErrorMessage = (error: any) => {
    return error?.code === 'auth/invalid-verification-code' ? t('Invalid-verification-code') : t('Something-went-wrong-please-try-again-later');
  };

  useEffect(() => {
    const recaptchaContainerId = '2fa-recaptcha-container';
    let createdWebContainer: HTMLElement | null = null;
    let verifierTarget: string | HTMLElement = recaptchaContainerId;

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      let webContainer = document.getElementById(recaptchaContainerId) as HTMLElement | null;

      if (!webContainer) {
        webContainer = document.createElement('div');
        webContainer.id = recaptchaContainerId;
        Object.assign(webContainer.style, {
          position: 'fixed',
          right: '16px',
          bottom: '16px',
          zIndex: '5000',
          width: '256px',
          minHeight: '60px',
        });
        document.body.appendChild(webContainer);
        createdWebContainer = webContainer;
      }

      verifierTarget = webContainer;
    }

    const verifier = new RecaptchaVerifier(auth, verifierTarget as any, {
      size: 'invisible',
      badge: 'bottomright',
      callback: (response) => {},
    });

    setRecaptchaVerifier(verifier);

    return () => {
      try {
        verifier.clear();
      } catch {
        // no-op: verifier cleanup is best-effort on web
      }

      if (createdWebContainer?.parentNode) {
        createdWebContainer.parentNode.removeChild(createdWebContainer);
      }
    };
  }, []);

  const gotLoginInfo = (response) => {
    setLoading(false);

    if (response.code == 'auth/wrong-password') {
      setUserMessage(tLogin('invalid-credentials'));
      return;
    }

    if (response.code === 'auth/too-many-requests') {
      setUserMessage(tLogin('too-many-request-login'));
      return;
    }

    if (response.code == 'auth/user-not-found') {
      setUserMessage(tLogin('invalid-credentials'));
      return;
    }

    if (response.code == 'auth/multi-factor-auth-required') {
      if (phoneVerificationId == undefined) {
        let resolver = getMultiFactorResolver(auth, response);
        setResolver(resolver);
        let phoneInfoOptions = {
          multiFactorHint: resolver.hints[0],
          session: resolver.session,
        };
        setPhoneInfoOptions(phoneInfoOptions);
        setVerificationCodeError(undefined);
        setPhoneVerificationId(undefined);
        const phoneAuthProvider = new PhoneAuthProvider(auth);

        try {
          phoneAuthProvider
            .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            .then(function (verificationId) {
              setPhoneVerificationId(verificationId);
            })
            .catch(() => {
              setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
            })
            .finally(() => {
              setLoading(false);
            });
        } catch {
          setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
          setLoading(false);
        }
      }
      setShow2fa(true);
      return;
    }

    if (response.email || response.user.email) {
      props.onReauthenticate && props.onReauthenticate();
    }
  };

  const verify2fa = () => {
    if (loading) {
      return;
    }

    setVerificationCodeError(undefined);

    if (!resolver || !phoneVerificationId) {
      setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
      return;
    }

    if (verificationCode.length !== 6) {
      setVerificationCodeError(t('Invalid-verification-code'));
      return;
    }

    setLoading(true);

    let multiFactorAssertion;
    try {
      const cred = PhoneAuthProvider.credential(phoneVerificationId, verificationCode);
      multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    } catch (error) {
      setVerificationCodeError(getVerificationErrorMessage(error));
      setLoading(false);
      return;
    }

    (resolver as any)
      .resolveSignIn(multiFactorAssertion)
      .then(function (userCredential) {
        setPhoneVerificationId(undefined);
        gotLoginInfo(userCredential);
      })
      .catch(function (error) {
        setVerificationCodeError(getVerificationErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReauthenticate = (email, password) => {
    if (!email || email.trim() === '') {
      setEmailLoginValidationError(tLogin('email_required'));
      return;
    }

    if (!password || password.trim() === '') {
      setPasswordLoginValidationError(tLogin('password_required'));
      return;
    }

    if (email?.toLocaleLowerCase() != authStore?.user?.email?.toLocaleLowerCase()) {
      setUserMessage(tLogin('invalid-credentials'));
      return;
    }

    if (email && password) {
      setUserMessage(undefined);
      setLoading(true);
      dispatch(authActions.reauthenticate({ email: email.trim(), password, callback: gotLoginInfo }));
    }
  };

  return (
    <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
      {Platform.OS !== 'web' && (
        <View id={'2fa-recaptcha-container'} style={{ width: 0, height: 0 }}>
        {/* recaptcha used for account verification */}
        </View>
      )}
      {!show2fa ? (
        <>
          <ScrollView style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Reauthenticate')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('Please-log-back-in')}
            </Label>
            <>
              {userMessage && (userMessage?.length ?? 0) > 0 && (
                <Label
                  numberOfLines={5}
                  variant={LabelType.titleSmall}
                  style={{ padding: 16, marginTop: 24, backgroundColor: theme.primary, ...StylesHelper.borderRadius(8) }}
                >
                  {userMessage}
                </Label>
              )}
            </>
            <Label style={{ fontSize: 14, marginTop: 8 }}>{t('email')}</Label>
            <CustomTextInput
              style={{ marginTop: 4 }}
              required
              showErrorMessage
              hasError={!!emailLoginValidationError}
              errorMessage={emailLoginValidationError}
              inputTypeValidation={InputTypeValidation.email}
              onValidationChange={(v) => (v.valid ? setEmailLoginValidationError(undefined) : setEmailLoginValidationError(v.message))}
              placeholder={t('email')}
              requiredMessage={tLogin('email_required')}
              key={2}
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setEmailLoginValidationError(undefined);
              }}
              onKeyPress={(e) => {
                {
                  if ((e as any).code === 'Enter') {
                    handleReauthenticate(email, password);
                  }
                }
              }}
              returnKeyType="next"
              onSubmitEditing={() => {
                secondInput.current.focus();
              }}
            />
            <Label style={{ fontSize: 14, marginTop: 8 }}>{t('password')}</Label>
            <CustomTextInput
              ref={secondInput}
              style={{ marginTop: 4 }}
              required
              showErrorMessage
              hasError={!!(passwordLoginValidationError || passwordLoginInputValidationError)}
              errorMessage={passwordLoginValidationError ? passwordLoginValidationError : passwordLoginInputValidationError}
              requiredMessage={tLogin('password_required')}
              onValidationChange={(v) => (v.valid ? setPasswordLoginInputValidationError(undefined) : setPasswordLoginInputValidationError(v.message))}
              secureTextEntry={true}
              placeholder={t('password')}
              key={1}
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setPasswordLoginValidationError(undefined);
                setPasswordLoginInputValidationError(undefined);
              }}
              onKeyPress={(e) => {
                if ((e as any).code === 'Enter') {
                  handleReauthenticate(email, password);
                }
              }}
              returnKeyType="done"
              onSubmitEditing={() => {
                handleReauthenticate(email, password);
              }}
            />
          </ScrollView>
          {loading ? (
            <View style={{ width: '100%', height: 50 }}>
              <Loading />
            </View>
          ) : (
            <CustomButton
              style={popupButtonStyle}
              textStyle={{ color: 'white' }}
              title={t('Reauthenticate')}
              onPress={() => handleReauthenticate(email, password)}
            />
          )}
        </>
      ) : (
        <>
          <View style={{ marginBottom: 24 }}>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('We-have-sent-a-verification-code-to')}
            </Label>
            <Label style={popupBodyEmphasisStyle} numberOfLines={10}>
              {`${phoneInfoOptions.multiFactorHint.phoneNumber}`}
            </Label>
            <CustomTextInput
              style={{ marginTop: 24, marginBottom: 24 }}
              placeholder={t('Verification-Code')}
              keyboardType="number-pad"
              maxLength={6}
              showErrorMessage
              hasError={!!verificationCodeError}
              errorMessage={verificationCodeError}
              onChangeText={(text) => {
                setVerificationCode(normalizeVerificationCode(text));
                setVerificationCodeError(undefined);
              }}
              value={verificationCode}
              returnKeyType="done"
              onSubmitEditing={() => {
                verify2fa();
              }}
            />
          </View>
          {loading ? (
            <View style={{ width: '100%', height: 50 }}>
              <Loading />
            </View>
          ) : (
            <CustomButton
              style={popupButtonStyle}
              textStyle={{ color: 'white' }}
              title={t('Reauthenticate')}
              onPress={verify2fa}
            />
          )}
        </>
      )}
    </View>
  );
};

export default ReauthenticateUserForm;
